import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { creditsAPI } from '../services/api';
import { FaServer, FaGift, FaComment, FaBell, FaCog, FaUser, FaSignOutAlt } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getProfilePicture } from '../utils/helpers';
import { toast } from 'react-toastify';
import PaymentModal from '../components/PaymentModal';
import Header from '../components/Header';

const Credits = () => {
  const navigate = useNavigate();
  const { user, logout, refreshCredits } = useAuth();

  // Debug user changes
  useEffect(() => {
    console.log('👤 Credits Header - User changed:', user?.id, user?.profile_picture, user?.credits);
  }, [user]);

  // Sync balance with AuthContext credits (for backward compatibility)
  useEffect(() => {
    if (user?.credits !== undefined) {
      console.log('💰 Syncing balance with AuthContext credits:', user.credits);
      setBalance(user.credits);
    }
  }, [user?.credits]);
  const [pricing, setPricing] = useState([]);
  const [balance, setBalance] = useState(0);

  // Get current credits from AuthContext or local state
  const currentCredits = user?.credits ?? balance;
  const [loading, setLoading] = useState(true);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  // Update balance when user changes (e.g., after login or page refresh)
  useEffect(() => {
    if (user?.credits !== undefined && !loading) {
      setBalance(user.credits);
    }
  }, [user?.credits, loading]);

  const fetchData = async () => {
    try {
      // Only fetch pricing, use credits from AuthContext
      const pricingRes = await creditsAPI.getPricing();
      setPricing(pricingRes.pricing || []);

      // Use credits from AuthContext, but refresh it to ensure it's current
      if (refreshCredits) {
        await refreshCredits();
      }
    } catch (error) {
      console.error('Failed to fetch pricing:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = (pkg) => {
    setSelectedPackage(pkg);
    setShowPaymentModal(true);
  };

  const handlePaymentSuccess = async () => {
    toast.success('Credits purchased successfully!');
    setShowPaymentModal(false);

    // Credits bilgisini gerçek zamanlı güncelle (gift modal gibi)
    if (refreshCredits) {
      await refreshCredits();
    }
  };

  const handleStickerPackPurchase = async (packType) => {
    const packPrices = {
      'basic': 20,
      'premium': 50,
      'vip': 100
    };

    const price = packPrices[packType];
    if (!price) return;

    // Check if user has enough credits
    const currentCredits = user?.credits ?? balance;
    if (currentCredits < price) {
      toast.error(`You need ${price} credits for this sticker pack. You have ${currentCredits} credits.`);
      return;
    }

    try {
      setLoading(true);
      await creditsAPI.purchaseStickerPack({
        pack_type: packType,
        credits: price
      });

      toast.success(`${packType.charAt(0).toUpperCase() + packType.slice(1)} sticker pack purchased successfully!`);

      // Refresh credits
      if (refreshCredits) {
        await refreshCredits();
      }
    } catch (error) {
      console.error('Failed to purchase sticker pack:', error);
      toast.error('Failed to purchase sticker pack');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="wrapper">
        <div className="section">
          <div className="container text-center">
            <h3>Loading...</h3>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="matchus-credits min-h-screen bg-gray-50">
      <Header title="Credits" />

      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Header */}

        {/* Current Balance */}
        <div className="bg-gradient-to-r from-pink-500 to-pink-600 rounded-lg p-6 mb-8 text-center text-white">
          <div className="flex items-center justify-center mb-2">
            <FaServer className="text-2xl mr-2" />
            <span className="text-3xl font-bold">
              {loading ? '...' : (user?.credits ?? balance)}
            </span>
          </div>
          <p className="text-pink-100">
            {loading ? 'Loading Credits...' : 'Available Credits'}
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="mb-12">
          <h2 className="text-2xl font-semibold text-gray-900 text-center mb-8">Choose Your Package</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {pricing.map((pkg) => (
              <div
                key={pkg.id}
                className="bg-white rounded-lg shadow-md border border-gray-200 hover:shadow-lg transition-shadow duration-300 overflow-hidden"
              >
                <div className="p-6 text-center">
                  <div className="mb-4">
                    <div className="text-4xl font-bold text-pink-500 mb-1">
                      {pkg.credits}
                    </div>
                    <div className="text-lg text-gray-600">Credits</div>
                  </div>

                  <div className="mb-6">
                    <div className="text-3xl font-bold text-gray-900 mb-1">
                      ${pkg.price}
                  </div>
                </div>

                            <button
                    className="w-full bg-pink-500 hover:bg-pink-600 text-white font-medium py-3 px-6 rounded-md transition-colors duration-200"
                              onClick={() => handlePurchase(pkg)}
                            >
                    Purchase Now
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

        {/* Sticker Packs Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 mb-8">
          <h2 className="text-2xl font-semibold text-gray-900 text-center mb-8">
            Sticker Packs
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
              <div className="bg-green-50 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">😊</span>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Basic Pack</h3>
              <p className="text-gray-600 mb-4">Fun everyday stickers for casual chats</p>
              <div className="text-sm text-green-600 font-medium mb-4">20 credits</div>
              <button
                onClick={() => handleStickerPackPurchase('basic')}
                disabled={loading || (user?.credits ?? balance) < 20}
                className="w-full bg-green-500 hover:bg-green-600 disabled:bg-green-300 text-white font-medium py-2 px-4 rounded-md transition-colors duration-200"
              >
                {loading ? 'Purchasing...' : 'Purchase'}
              </button>
            </div>

            <div className="text-center border border-purple-200 rounded-lg p-6 hover:shadow-md transition-shadow bg-purple-50">
              <div className="bg-purple-50 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">🎨</span>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Premium Pack</h3>
              <p className="text-gray-600 mb-4">Beautiful animated stickers for special moments</p>
              <div className="text-sm text-purple-600 font-medium mb-4">50 credits</div>
              <button
                onClick={() => handleStickerPackPurchase('premium')}
                disabled={loading || (user?.credits ?? balance) < 50}
                className="w-full bg-purple-500 hover:bg-purple-600 disabled:bg-purple-300 text-white font-medium py-2 px-4 rounded-md transition-colors duration-200"
              >
                {loading ? 'Purchasing...' : 'Purchase'}
              </button>
            </div>

            <div className="text-center border border-yellow-200 rounded-lg p-6 hover:shadow-md transition-shadow bg-yellow-50">
              <div className="bg-yellow-50 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">👑</span>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">VIP Pack</h3>
              <p className="text-gray-600 mb-4">Exclusive VIP stickers with special effects</p>
              <div className="text-sm text-yellow-600 font-medium mb-4">100 credits</div>
              <button
                onClick={() => handleStickerPackPurchase('vip')}
                disabled={loading || (user?.credits ?? balance) < 100}
                className="w-full bg-yellow-500 hover:bg-yellow-600 disabled:bg-yellow-300 text-white font-medium py-2 px-4 rounded-md transition-colors duration-200"
              >
                {loading ? 'Purchasing...' : 'Purchase'}
              </button>
            </div>
          </div>
        </div>

        {/* Features Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
          <h2 className="text-2xl font-semibold text-gray-900 text-center mb-8">
            What Can You Do With Credits?
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Send Gifts */}
            <div className="text-center">
              <div className="bg-pink-50 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
                <FaGift className="text-3xl text-pink-500" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Send Gifts</h3>
              <p className="text-gray-600 mb-2">
                Express your feelings with beautiful gifts to other users
              </p>
              <div className="text-sm text-pink-600 font-medium">
                10 credits per gift
              </div>
                    </div>

            {/* Send Messages */}
            <div className="text-center">
              <div className="bg-blue-50 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
                <FaComment className="text-3xl text-blue-500" />
                    </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Premium Messages</h3>
              <p className="text-gray-600 mb-2">
                Send unlimited messages and get priority responses
              </p>
              <div className="text-sm text-blue-600 font-medium">
                5 credits per message
                    </div>
                  </div>

            {/* Unlock Stickers */}
            <div className="text-center">
              <div className="bg-purple-50 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
                <div className="text-3xl text-purple-500">🎭</div>
                </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Sticker Packs</h3>
              <p className="text-gray-600 mb-2">
                Unlock fun sticker packs to make conversations more lively
              </p>
              <div className="text-sm text-purple-600 font-medium">
                20 credits per pack
              </div>
            </div>
          </div>

          {/* Additional Info */}
          <div className="mt-8 p-4 bg-gray-50 rounded-lg">
            <div className="text-center text-gray-600">
              <p className="mb-2">
                💡 <strong>Tip:</strong> Credits never expire and can be used anytime!
              </p>
              <p>
                🔒 All purchases are secure and processed through encrypted payment gateways.
              </p>
            </div>
          </div>
        </div>
      </div>

      {showPaymentModal && (
        <PaymentModal
          isOpen={showPaymentModal}
          onClose={() => setShowPaymentModal(false)}
          package={selectedPackage}
          type="credits"
          onSuccess={handlePaymentSuccess}
        />
      )}
    </div>
  );
};

export default Credits;

